<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, Ref, ref, watch } from 'vue';
import i18n from '@/localization/i18n';

import BusinessRequestProgressBar from "./BusinessRequestProgressBar.vue";
import { storeToRefs } from 'pinia';
import { useBusinessRequestFormStore } from '@/pages/index/stores/business-request-form-store';
import { isAuth } from '@/utils/functions';
import { useUserStore } from '@/stores/user';
import { useAlertsStore } from '@/stores/alerts';
import { logNewEvent } from '@/services/modules/firebase';

const userStore = useUserStore()
const user = userStore.getUser

const businessRequestFormStore = useBusinessRequestFormStore()
const { keyswordFormData } = storeToRefs(businessRequestFormStore)

const tagsList: Ref<any> = ref([])
const keywordsArray: Ref<any[]> = ref([])

const searchBarFocused = ref(false)
const keywords: Ref<any> = ref(null)
const hasChanges: Ref<boolean> = ref(false)

const addKeyWords = ref(false)
const selectedOption = ref<number | null>(null);

const isUserAuthenticated = ref(false)
const isLoading = ref(false)
    
const t = i18n.global.t

const mobileMaxWIdth = 1300
const isMobile: Ref<boolean> = ref(false)

onMounted(() => {
    isUserAuthenticated.value = isAuth()
    businessRequestFormStore.wasUserAuthenticatedBeforeStarting = isUserAuthenticated.value ? 1 : 0
    tagsList.value = keyswordFormData.value.tagsList
    keywordsArray.value = keyswordFormData.value.keywordsArray
    keywords.value = keyswordFormData.value.keywords
    selectedOption.value = keyswordFormData.value.selectedOption

    isMobile.value = window.innerWidth <= mobileMaxWIdth
    window.addEventListener('resize', handleResize);
})

const unwatchTaglist = watch(tagsList, (newVal) => {
    keyswordFormData.value.tagsList = newVal
})

const unwatchKeywordsArray = watch(keywordsArray, (newVal) => {
    keyswordFormData.value.keywordsArray = newVal
})

const unwatchKeywords = watch(keywords, (newVal) => {
    keyswordFormData.value.keywords = newVal
})

const onSearchBarFocus = () => {
    searchBarFocused.value = true
}

const onSearchBarBlur = () => {
    searchBarFocused.value = false
}

const MAX_TOTAL_LENGTH = 126;
const totalTagsLength = computed(() => {
  return tagsList.value.reduce((acc:any, tag:any) => acc + tag.length, 0);
});

// Dynamic remaining characters based on existing keywords length
const remainingCharacters = computed(() => {
  return Math.max(MAX_TOTAL_LENGTH - totalTagsLength.value, 0);
});

const closeChip = (index: number) => {
    tagsList.value.splice(index, 1)
    keyswordFormData.value.tagsList = tagsList.value
    keywordsArray.value = {...tagsList.value}
    hasChanges.value = true
};

function containsSeparator(str: string) {
    const separators = [",", ";", " ", "."];
    
    for (let separator of separators) {
        if (str.includes(separator)) {
            return true;
        }
    }
    
    return false;
}

const onInput = () => {
    const separators = /[,.; ]+/
    if (keywords.value) {
        if (keywords.value.length > remainingCharacters.value) {
            keywords.value = keywords.value.slice(0, remainingCharacters.value); // Truncate the input if it exceeds allowed length
        }
        if (containsSeparator(keywords.value)) {
            const listKeywords = keywords.value.split(separators)
            const filteredArray = listKeywords.filter((element: any) => element !== "");
            for (let i = 0; i < filteredArray.length; i++) {
                tagsList.value.push(filteredArray[i])
                keywordsArray.value.push(filteredArray[i])
            }
            keywords.value = null
            keywordsArray.value = {...tagsList.value}
        } else {
            keywordsArray.value = [...keywords.value]
        }
        hasChanges.value = true
    }
};

const onPaste = (event:any) =>{
    event.preventDefault();

    const data = event.clipboardData.getData('text');

    if(data){
        let pastedData = data.slice(0, remainingCharacters.value) 

        const separators = /[,.; ]+/;
        let listKeywords = pastedData.split(separators);
        
        let filteredArray = listKeywords.filter((element:any) => element !== "").slice(0, 3 - tagsList.value.length);

        for (let i = 0; i < filteredArray.length; i++) {
            tagsList.value.push(filteredArray[i])
        }

        keywordsArray.value = {...tagsList.value}
        hasChanges.value = true
    }
}

watch(selectedOption, () => {
    addKeyWords.value = selectedOption.value == 1 
    keyswordFormData.value.selectedOption = selectedOption.value
    if(!addKeyWords.value){
        keywords.value = null
        tagsList.value = []
        keywordsArray.value = []
    }
})

const autoSetKeyword = () =>{
    const separators = /[,.; ]+/
    if(keywords.value){
        if (keywords.value.length > remainingCharacters.value) {
            keywords.value = keywords.value.slice(0, remainingCharacters.value); // Truncate the input if it exceeds allowed length
        }
        const listKeywords = keywords.value.split(separators)
        const filteredArray = listKeywords.filter((element: any) => element !== "");
        if(filteredArray.length != 0){
             tagsList.value.push(filteredArray[0])
            keywordsArray.value = [...tagsList.value]
            keywords.value = null
        }
    }
    keyswordFormData.value.tagsList = tagsList.value
    keyswordFormData.value.keywordsArray = keywordsArray.value
}

const next = () =>{
    autoSetKeyword()
    if(isUserAuthenticated.value){
        //submit the form
        isLoading.value = true
        
        let payload = businessRequestFormStore.constructPayload(user)
        let filesArray = businessRequestFormStore.imagesPayload()

        try {
            businessRequestFormStore.submit(payload,filesArray).then((response) =>{
            if(response !== null){
                const alerts = useAlertsStore()
                alerts.putAlert({ 'code': 'businessRequestSaveChangesFail', 'scope': 'general' })
                isLoading.value = false
                return
            }
            //if success
            isLoading.value = false
            logNewEvent("presta_request_submit_tags")
            //show the last step
            businessRequestFormStore.next_step('confirmation')
        })
        } catch (error) {
            const alerts = useAlertsStore()
            alerts.putAlert({ 'code': 'businessRequestSaveChangesFail', 'scope': 'general' })
            isLoading.value = false
            return
        }
    }else{
        businessRequestFormStore.next_step('signup')
    }
    
}

const back = () =>{
    businessRequestFormStore.prev_step('images')
}

const handleResize = () => {
  isMobile.value = window.innerWidth <= mobileMaxWIdth 
};

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  unwatchKeywords()
  unwatchKeywordsArray()
  unwatchTaglist()
});

const showBaseOnLength = (): boolean =>{
    if(tagsList.value.length > 0) return true

    let length:number=20
    let windowsWidth = window.innerWidth
    if(!isMobile.value) length = 40

    if(windowsWidth > 1700){
        length = 80
    }

    if(tagsList.value.length === 0) return false
    let words:string = ""

    for(let i=0;i<tagsList.value.length;i++){
        words += tagsList.value[i]
    }
    
    return words.length >= length 
}

</script>

<template>
    <div class="rounded main fullHeight" style="height: 550px">
        <BusinessRequestProgressBar />
        <div class="fullRemainingHeight">
            <div class="expanded">
                <div class="h-style h3-style use-poppins text-center mt-2">
                    {{ t("w_txt_business_request_adding_key_words") }}
                </div>
                <div class="mt-3 mx-md-5">
                    <p class="">
                        {{ t("w_txt_business_request_key_words_overview") }}
                    </p>
                </div>
                <div class="card shadow rounded-3 mt-3 py-2 px-4 w-100 mt-4">
                    <div class="card-content">
                        <div class="mb-2 flex-space-between">
                            <div class="form-check">
                                <input v-model="selectedOption" class="form-check-input" type="radio" name="exampleRadios" id="choices1" value="1" >
                                <label class="form-check-label" for="choices1">
                                    {{ t('w_txt_business_request_yes') }}
                                </label>
                            </div>
                        </div>
                        <div class="mb-2 flex-space-between">
                            <div class="form-check">
                                <input v-model="selectedOption" class="form-check-input" type="radio" name="exampleRadios" id="choices2" value="2" >
                                <label class="form-check-label" for="choices2">
                                    {{ t('w_txt_business_request_no_maybe_later') }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="addKeyWords">
                    <div v-if="tagsList.length >= 3" class="col-12 mb-2 mt-4">
                        <p class="left-text">
                            {{ t('w_frm_wkSearch_keywords') }}
                        </p>
                        <div class="text-start mb-0 d-flex flex-wrap p rounded-0 no-background" style="min-height: 2.5rem;">
                            <div v-for="(keyword, index) in tagsList" 
                                class="btn btn-info btn-sm rounded-pill me-1 d-flex align-items-center mb-2"
                                style="flex: 0 1 auto; max-width: 100%;"
                            >
                                <span 
                                    style="cursor: default; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;"
                                    :title="keyword"
                                >
                                    {{ keyword }}
                                </span>
                                <span class="ps-2" @click="closeChip(index)">
                                    <font-awesome-icon icon="fa-solid fa-xmark" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else :class="{ 'searchBarFocus': searchBarFocused  }" class="form-control text-start mb-0 d-flex mt-5" style="min-height: 2.5rem;">
                        <div v-if="!showBaseOnLength()" v-for="(keyword, index) in tagsList" 
                            class="btn btn-info btn-sm rounded-pill me-1 d-flex align-items-center">
                            <span style="cursor: default;">{{ keyword }}</span>
                            <span class="ps-1" @click="closeChip(index)">
                                <font-awesome-icon icon="fa-solid fa-xmark" />
                            </span> 
                        </div>
                        <input type="text"
                            v-if="tagsList.length < 3"
                            v-model="keywords"
                            :maxlength="remainingCharacters"
                            @input="onInput"
                            @focus="onSearchBarFocus"
                            @blur="onSearchBarBlur"
                            @paste="onPaste"
                            :placeholder="tagsList.length > 0 ? (showBaseOnLength()) ? t('w_txt_csBusinessRequestKeywordsPlaceholder'): '' : t('w_txt_csBusinessRequestKeywordsPlaceholder')"
                            class="chip-input" 
                            style="border: 0px; flex: 1;">
                    </div>
                    <div v-if="showBaseOnLength() && tagsList.length < 3" class="col-12 mb-2 mt-2">
                        <div class="text-start mb-0 d-flex flex-wrap p rounded-0 no-background" style="min-height: 2.5rem;">
                            <div v-for="(keyword, index) in tagsList" 
                                class="btn btn-info btn-sm rounded-pill me-1 d-flex align-items-center mb-2"
                                style="flex: 0 1 auto; max-width: 100%;"
                            >
                                <span 
                                    style="cursor: default; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;"
                                    :title="keyword"
                                >
                                    {{ keyword }}
                                </span>
                                <span class="ps-1" @click="closeChip(index)">
                                    <font-awesome-icon icon="fa-solid fa-xmark" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style="margin-top: auto; margin-bottom:15px ">
                <button @click="back" type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-2 mb-2 custom-btn-width">
                    {{ t("w_act_business_request_previous") }}
                </button>

                <button @click="next" type="button" class="btn btn-primary mt-2 mb-2 on-action-btn custom-btn-width">
                    <span v-if="!isLoading">
                        {{ t( isUserAuthenticated ? "w_act_home_business_request_input_submit" : "w_act_business_request_next") }}
                    </span>
                    <div v-else class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </button>
            </div>
        </div>

    </div>
</template>

<style scoped>
.btn-primary:focus{
    background-color: #FEB23C !important;
}
.left-text{
    text-align: left !important;
    margin-bottom: 10px;
}

.searchBarFocus {
    border-color: #FEB23C !important;
    box-shadow: none Im !important;
}

.form-control:focus {
    border-color: #FEB23C;
    box-shadow: 0 0 0 0.02rem #FEB23C;
}

.form-control {
    text-align: start;
}

    .chip-input:focus {
    outline: none;
    border: none;
}

.chip-input:focus {
    outline: none;
    border: none;
}

.chip-input::placeholder {
    transition: all 0.2s ease;
}

.chip-input:focus::placeholder {
    transform: translate(5px);
    transition: all 0.2s ease;
}
.flex-space-between {
    display: flex;
    justify-content: space-between; 
    align-items: center; 
}

.card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;  
    padding: 1rem;            
}

.card-content{
    width: 100% !important;
}

.card-content p {
    text-align: left;
    margin-bottom: 0px;         
}

.main {
    padding-top: 15px;
    position: relative;
}

.bg {
    background-color: #FEB23C !important;
}

.fullHeight {
    height: 100%;
    z-index: 1px;
    border: none !important;
    display: flex;                  
    flex-direction: column;
}

.fullRemainingHeight {
    display: flex;                  
    flex-direction: column; 
    height: 100%;  
}

.expanded{
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.custom-btn-width {
    min-width: 130px;
}

/* Medium devices (md) - 768px and up */
@media (min-width: 768px) {
    /* Styles for medium screens */
    .expanded{
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    .custom-btn-width {
        min-width: 150px !important;
    }
}
</style>